import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners
} from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import Aura from '@primeuix/themes/aura';
import { providePrimeNG } from 'primeng/config';
import { ConfirmationService, MessageService } from 'primeng/api';

import { routes } from './app.routes';
import { AuthService } from './core/services/auth.service';
import { ConfirmDialogHotkeysService } from './core/services/confirm-dialog-hotkeys.service';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { trimInterceptor } from './core/interceptors/trim.interceptor';
import { GlobalErrorHandler } from './core/services/global-error-handler.service';
import { API_URL } from './core/tokens/api-url.token';
import { environment } from '../environments/environment';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: API_URL, useValue: environment.apiUrl },
    provideRouter(routes, withInMemoryScrolling({ anchorScrolling: 'enabled', scrollPositionRestoration: 'enabled' })),
    provideAnimationsAsync(),
    /**
     * Переклад — ТІЛЬКИ календарні ключі, і це межа навмисна.
     *
     * `Translation` у PrimeNG один на застосунок, і в ньому поруч із датами лежать дефолти,
     * якими годуються `p-confirmDialog` (`accept`/`reject`) і кожен `p-select` без власного
     * `emptyMessage`. Переклавши їх тут, ми б тихо змінили екрани, яких ця задача не
     * стосується, — і розійшлися б із рядком `'Завантаження...'`, який селекти биндять самі.
     *
     * `firstDayOfWeek: 1` — не косметика: сітка з неділі зміщує весь тиждень на день, і
     * оператор клікає сусідню дату, не помітивши. `dayNames*` PrimeNG індексує від НЕДІЛІ
     * незалежно від `firstDayOfWeek` (`createWeekDays()` сам крутить масив), тож порядок
     * нижче саме такий.
     */
    providePrimeNG({
      theme: { preset: Aura, options: { darkModeSelector: '.app-dark' } },
      translation: {
        firstDayOfWeek: 1,
        dayNames: ['Неділя', 'Понеділок', 'Вівторок', 'Середа', 'Четвер', 'Пʼятниця', 'Субота'],
        dayNamesShort: ['Нед', 'Пон', 'Вів', 'Сер', 'Чет', 'Пʼят', 'Суб'],
        dayNamesMin: ['Нд', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
        monthNames: [
          'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
          'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень',
        ],
        monthNamesShort: [
          'Січ', 'Лют', 'Бер', 'Кві', 'Тра', 'Чер', 'Лип', 'Сер', 'Вер', 'Жов', 'Лис', 'Гру',
        ],
        today: 'Сьогодні',
        clear: 'Очистити',
        weekHeader: 'Тиж',
        chooseYear: 'Обрати рік',
        chooseMonth: 'Обрати місяць',
        chooseDate: 'Обрати дату',
        prevMonth: 'Попередній місяць',
        nextMonth: 'Наступний місяць',
        prevYear: 'Попередній рік',
        nextYear: 'Наступний рік',
        prevDecade: 'Попереднє десятиліття',
        nextDecade: 'Наступне десятиліття',
      },
    }),
    MessageService,
    ConfirmationService,
    provideHttpClient(
      withInterceptors([trimInterceptor, errorInterceptor])
    ),
    provideAppInitializer(() => {
      const confirmDialogHotkeysService = inject(ConfirmDialogHotkeysService);
      confirmDialogHotkeysService.initialize();
    }),
    provideAppInitializer(() => {
      const authService = inject(AuthService);
      return authService.initialize();
    }),
  ]
};
